import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { ShippingService } from '../../services/api-services/shipping.service';
import { ToastrService } from 'ngx-toastr';
import { PatientRemainModalComponent } from './patient-remain-modal/patient-remain-modal.component';
@Component({
  selector: 'app-address-match-popup',
  templateUrl: './address-match-popup.component.html',
  styleUrl: './address-match-popup.component.scss'
})
export class AddressMatchPopupComponent implements OnInit {
  dataList: any[] = [];
  selectedIndex = 0;
  selectedAddress: string = '';
  selected: any;
  reviewStatus: ('' | 'warning' | 'success')[] = [];
  isLoading: boolean = false;
  isKrollRefreshing: boolean = false;
  updateResponses: any[] = [];  // Collect all responses
  selectedAddressMap: { [index: number]: string } = {};


  constructor(public dialogRef: MatDialogRef<AddressMatchPopupComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    public shippingService: ShippingService,
    public toastr: ToastrService,
    public dialog: MatDialog,) {
  }

  ngOnInit(): void {
    this.dataList = this.data?.item || [];
    this.selected = this.dataList[this.selectedIndex];
    this.selectedAddress = this.selected.krollAddress;
    this.reviewStatus = this.dataList.map(() => ''); // start review status with 'warning'
  }

  closeModal() {
    this.dialogRef.close({ success: false, message: 'Modal closed by user' });
  }

  selectPatient(index: number) {
    this.selectedIndex = index;
    this.selected = this.dataList[index];
    const previouslySaved = this.selectedAddressMap[index];
    if(previouslySaved){
      this.selectedAddress = previouslySaved; // use saved address if exists
    } else {
      this.selectedAddress = this.selected.krollAddress; // fallback to kroll address
    }
  }

  copyToClipboard(text: string, tooltip: MatTooltip) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        tooltip.disabled = false;     // enable manually
        tooltip.show();
        setTimeout(() => {
          tooltip.hide();
          tooltip.disabled = true;    // disable again
        }, 1500);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        tooltip.disabled = false;
        tooltip.show();
        setTimeout(() => {
          tooltip.hide();
          tooltip.disabled = true;
        }, 1500);
      } catch (err) {
        console.error('Fallback copy failed:', err);
      }
      document.body.removeChild(textarea);
    }
  }

  skip(): void {
    this.reviewStatus[this.selectedIndex] = 'warning';
    if (this.selectedIndex < this.dataList.length - 1) {
      this.selectedIndex++;
      this.selected = this.dataList[this.selectedIndex];
      this.selectedAddress = this.selected.krollAddress;
    }
  }

  isUserAddressSelected(): boolean {
    return this.selected?.userAddress?.some((addr: { userAddress: string; }) => addr.userAddress == this.selectedAddress);
  }

  onSelectAddress(value: string) {
    this.selectedAddress = value;
  }

  continue(): void {
    const isUserAddrSelected = this.isUserAddressSelected();
    if (isUserAddrSelected) {
      const selectedUserAddr = this.selected?.userAddress?.find((addr: { userAddress: string }) => addr.userAddress == this.selectedAddress);
      if (selectedUserAddr) {
        const payload = {
          order_id: this.selected?.salesOrderID,
          address_id: selectedUserAddr.id,
          user_id: this.selected?.userInfo?.user_id,
        };
        this.updateShippingAddress(payload);
      }
    } else {
      // If Kroll address selected, use its id directly
      const krollAddressId = this.selected?.krollAddress?.id;
      if (krollAddressId) {
        const payload = {
          order_id: this.selected?.salesOrderID,
          address_id: krollAddressId,
          user_id: this.selected?.userInfo?.user_id,
        };
        this.updateShippingAddress(payload);
      } else {
       // No address selected, mark as success
        this.reviewStatus[this.selectedIndex] = 'success';
        this.goToNextItem();
      }
    }
  }

  updateShippingAddress(payload: { order_id: number; address_id: number; user_id: number; }, openRemainingOnSuccess: boolean = false) {
    this.isLoading = true;
    this.shippingService.update_order_shpping_address(payload).subscribe({
      next: (res => {
        this.isLoading = false;
        const index = this.updateResponses.findIndex(item =>
          item.payload.order_id == payload.order_id &&
          item.payload.address_id == payload.address_id &&
          item.payload.user_id == payload.user_id
        );
        const responseEntry = {
          payload,
          response: res
        };
        if (index !== -1) {
          this.updateResponses[index] = responseEntry;
        } else {
          this.updateResponses.push(responseEntry);
        }
        if (res?.body?.success) {
          this.toastr.success(res?.body?.message[0]);
          this.reviewStatus[this.selectedIndex] = 'success';
          this.selectedAddressMap[this.selectedIndex] = this.selectedAddress;
          if (openRemainingOnSuccess) {
            const remainingPatients = this.dataList.filter((_, index) => !this.reviewStatus[index]);
            if (remainingPatients.length > 0) {
              this.openRemainPatient(remainingPatients);
            } else {
              this.dialogRef.close(this.updateResponses);
            }
          } else {
            this.goToNextItem();
          }
        } else {
          this.toastr.error(res?.body?.message[0]);
        }
      }),
      error: (error => {
        this.isLoading = false;
        const index = this.updateResponses.findIndex(item =>
          item.payload.order_id == payload.order_id &&
          item.payload.address_id == payload.address_id &&
          item.payload.user_id == payload.user_id
        );

        const errorEntry = {
          payload,
          error: error
        };

        if (index !== -1) {
          this.updateResponses[index] = errorEntry;
        } else {
          this.updateResponses.push(errorEntry);
        }
        this.toastr.error('Failed to update address.');
        console.error('Address update error:', error);
      })
    });
  }

  refreshKrollAddr(payload: { order_id: number; address_id: number; user_id: number; }) {
    this.isKrollRefreshing = true;
    this.shippingService.refresh_Kroll_Addr(payload).subscribe({
      next: (res => {
        this.isKrollRefreshing = false;
        if (res?.body?.success) {
          this.selected.krollAddress = res?.body?.data;
          // Keep Kroll address selected after refresh by updating the reference
          this.selectedAddress = this.selected.krollAddress; // update selected address
          this.toastr.success(res?.body?.message[0]);
        } else {
          this.toastr.error(res?.body?.message[0]);
          console.error('Address refresh error:', res);
        }
      }),
      error: (error => {
        this.isKrollRefreshing = false;
        this.toastr.error('Failed to refresh address.');
        console.error('Address refresh error:', error);
      })
    });
  }

  goToNextItem() {
    if (this.selectedIndex < this.dataList.length - 1) {
      this.selectedIndex++;
      this.selected = this.dataList[this.selectedIndex];
      this.selectedAddress = this.selected.krollAddress;
    } else {
      // Close dialog and pass responses
      this.dialogRef.close(this.updateResponses);
    }
  }

  openRemainPatient(remainingPatients: any[]) {
    try {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.data = { patients: remainingPatients };
      dialogConfig.width = window.innerWidth < 768 ? "90%" : "35%";
      const dialogRef = this.dialog.open(PatientRemainModalComponent, dialogConfig);
      dialogRef.afterClosed().subscribe(res => {
        if (res?.val == 'true') {
          this.updateResponses.push(res);
          this.reviewStatus[this.selectedIndex] = 'success';
          this.goToNextItem();
        } else {
          // this.toastr.error('No patient selected to continue.');
        }
      });
    } catch (error) {
      console.log(error);
    }
  }

  onContinueAndClose() {
    const isUserAddrSelected = this.isUserAddressSelected();
    let payload: any;
    if (isUserAddrSelected) {
      const selectedUserAddr = this.selected?.userAddress?.find((addr: { userAddress: string }) => addr.userAddress == this.selectedAddress);
      if (selectedUserAddr) {
        payload = {
          order_id: this.selected?.salesOrderID,
          address_id: selectedUserAddr.id,
          user_id: this.selected?.userInfo?.user_id,
        };
      }
    } else {
      payload = {
        order_id: this.selected?.salesOrderID,
        address_id: this.selected?.krollAddress?.id,  // directly use the id from krollAddress
        user_id: this.selected?.userInfo?.user_id,
      };
    }

    // If payload valid and last patient
    if (payload && this.selectedIndex == this.dataList.length - 1) {
      this.updateShippingAddress(payload, true);
    } else if (payload) {
      this.updateShippingAddress(payload);
    } else {
      this.reviewStatus[this.selectedIndex] = 'success';
      if (this.selectedIndex == this.dataList.length - 1) {
        const remainingPatients = this.dataList.filter((_, index) => !this.reviewStatus[index]);
        if (remainingPatients.length > 0) {
          this.openRemainPatient(remainingPatients);
        } else {
          this.dialogRef.close(this.updateResponses);
        }
      } else {
        this.goToNextItem();
      }
    }
  }

}
